import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import moment from 'moment';
import jimp from 'jimp/es';
import BlobtoBuffer from 'blob-to-buffer';
import Axios from 'axios';
import isEmpty from 'lodash/isEmpty';
import isNull from 'lodash/isNull';
import Smartuploader from '../../../../components/Smartuploader';
import { soundcastManager } from '../../../../helpers/managers';

class ImageArt extends Component {
  constructor(props) {
    super(props);

    this.state = {
      url: '',
      urlBlur: '',
      urlPreview: '',
      cropped: false,
    };

    this.onFilepondProcess = this.onFilepondProcess.bind(this);
    this.onFilepondFileRemove = this.onFilepondFileRemove.bind(this);
    this.resetImageUri = this.resetImageUri.bind(this);
  }

  componentDidMount() {
    const { value } = this.props;
    this.setState({
      url: value,
    });
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.value !== nextProps.value) {
      this.setState({
        url: nextProps.value,
      });
    }
  }

  onFilepondProcess(error, filepond) {
    if (error) {
      return console.error('Unable to process file', error);
    }

    const { serverId, filename } = filepond;
    if (!isEmpty(serverId)) {
      if (serverId.slice(0,5) === 'Error') {
        console.error(`Unable to process file ${filename} ${serverId}`);
        return alert(`Unable to process file ${filename}`);
      }

      const { url } = JSON.parse(serverId);
      const { datakey } = this.props;
      this.setState({ url }, () => {
        // delete old and update it with new
        this.deleteAndUpdateOldFileTrace(datakey, url);

        // create blur version if set
        this.createBlurredVersion(filepond, `blurred-${filename}`);

        // create preview version if set
        this.createPreviewVersion(filepond, `preview-${filename}`);
      });
    }
  }

  onFilepondFileRemove() {
    const { url, urlBlur, urlPreview } = this.state;
    const { withBlur, withPreview } = this.props;

    if (!isEmpty(url) && !url.includes('dummyimage.com')) {
      this.deleteFileUpload(url, 'imageURL');

      // remove blur
      if (withBlur && !isEmpty(urlBlur)) {
        this.deleteFileUpload(urlBlur, 'blurredImageURL');
      }

      // remove preview
      if (withPreview && !isEmpty(urlPreview)) {
        this.deleteFileUpload(urlPreview, 'previewImage');
      }
    }

    // reset state
    this.resetImageUri();
  }

  resetImageUri() {
    this.setState({
      url: '',
      urlBlur: '',
      urlPreview: '',
      cropped: false,
    });
  }

  initFilepondImageCropperFor(filepond) {
    this.props.cropperHandler(({ close }) => {
      return {
        file: filepond.source,
        onCrop: fileblob => {
          // once we get the fileblob of the cropped image
          // create a new file and add it to filepond
          const { name, type } = filepond.source;
          const lastModified = Date.now();
          let croppedFile;
          try {
            croppedFile = new File([fileblob], name, { type, lastModified });
          } catch (err) {}
          if (!croppedFile) {
            try {
              croppedFile = new Blob([fileblob], name, { type, lastModified }); // MS Edge case
            } catch (err) {}
          }
          if (!croppedFile) {
            console.error('Image crop failed!');
            alert('Image crop failed!');
          }

          // console.log(croppedFile, fileblob, filepond);
          const targetref = this.fileuploaderRef;
          const uploaderRef = targetref.getUploaderRef('file');
          uploaderRef.getPond().addFile(croppedFile);

          this.setState({ cropped: true }, close);
        },
      };
    });
  }

  convertBlobToBuffer(blob) {
    return new Promise((resolve, reject) => {
      BlobtoBuffer(blob, (err, buffer) => {
        if (err) {
          return reject(err);
        }

        resolve(buffer);
      });
    });
  }

  processAndUploadImage(options = {}) {
    const { publisherId, soundcastId } = this.props || {};
    return new Promise(async (resolve, reject) => {
      try {
        const { key, blob, jimpHandler = () => null, filename, destinationField = '' } = options;

        // convert the source blob to buffer
        const imageBuffer = await this.convertBlobToBuffer(blob);

        // read the image buffer and modify
        const jimpBuffer = await jimp.read(imageBuffer);
        const modifiedJimpBuffer = await new Promise((resolve, reject) => {
          const customProccessedJimp = jimpHandler(jimpBuffer);
          if (isNull(customProccessedJimp)) {
            return reject(new TypeError('Unable to get a jimp instance'));
          }

          customProccessedJimp.getBuffer(jimp.AUTO, (err, buffer) => {
            if (err) {
              return reject(err);
            }

            resolve(buffer);
          });
        });

        // append modified image buffer to form data
        const formdata = new FormData();
        formdata.append('filepond', new Blob([modifiedJimpBuffer]), filename); // to files

        const filepondMetadata = { publisherId, soundcastId, destinationField, autoDeletePrevious: true };
        formdata.append('filepond', JSON.stringify(filepondMetadata)); // to body

        // now upload the file
        const { data } = await Axios.post('/api/upload', formdata);
        this.setState({ [key]: data.url }, () => {
          this.deleteAndUpdateOldFileTrace(key, data.url);
          resolve();
        });
      } catch (err) {
        console.log('ERROR creating blurred file:', err.message);
        reject(err);
      }
    });
  }

  async createBlurredVersion(filepond, blurredFilename) {
    try {
      const { withBlur, datakeyBlur } = this.props;
      const { filename, source: blob } = filepond;

      // if no blurred image do nothing
      if (!withBlur) {
        return false;
      }

      await this.processAndUploadImage({
        blob,
        key: datakeyBlur,
        filename: blurredFilename || `blurred-${filename}`,
        jimpHandler: ins => ins.resize(500, jimp.AUTO).blur(30).brightness(0.6),
        destinationField: 'blurredImageURL',
      });
    } catch (err) {
      console.log('ERROR creating blurred file:', err.message);
      return false;
    }
    return true;
  }

  async createPreviewVersion(filepond, previewFilename) {
    try {
      const { withPreview, datakeyPreview } = this.props;
      const { filename, source: blob } = filepond;

      // if no blurred image do nothing
      if (!withPreview) {
        return false;
      }

      await this.processAndUploadImage({
        blob,
        key: datakeyPreview,
        filename: previewFilename || `preview-${filename}`,
        jimpHandler: ins => ins.resize(300, jimp.AUTO),
        destinationField: 'previewImage',
      });
    } catch (err) {
      console.log('ERROR creating preview file:', err.message);
      return false;
    }
    return true;
  }

  async deleteAndUpdateOldFileTrace(key = '', newvalue = '') {
    try {
      if (isEmpty(key)) {
        return false;
      }

      const { soundcastId, autoDeletePrevious, autoSave, onChange } = this.props;
      onChange(key, newvalue);

      await Promise.all([
        // // deletes previous value of the image file
        // new Promise(async (resolve, reject) => {
        //   try {
        //     if (!autoDeletePrevious) {
        //       return resolve();
        //     }

        //     const soundcastKey = await soundcastManager.getProp(soundcastId, `${key}`);
        //     if (!isEmpty(soundcastKey)) {
        //       await this.deleteFileUpload(soundcastKey);
        //     }

        //     resolve();
        //   } catch (err) {
        //     reject(err);
        //   }
        // }),

        // auto save data to firebase when set
        new Promise(async resolve => {
          try {
            if (!autoSave) {
              return resolve();
            }

            await soundcastManager.saveProp(soundcastId, `${key}`, newvalue);
            resolve();
          } catch (err) {
            reject(err);
          }
        }),
      ]);
    } catch (e) {}
  }

  deleteFileUpload(url, destinationField = '') {
    return new Promise((resolve, reject) => {
      if (!url.includes('http')) {
        return reject();
      }

      const { soundcastId } = this.props || {};

      Axios.delete('/api/upload', {
        params: {
          url,
          soundcastId,
          destinationField,
        },
      })
        .then(resolve)
        .catch(reject);
    });
  }

  render() {
    const {
      publisherId,
      soundcastId,
      uploader,
      withCrop,
      artStyle,
      filename,
      allowEdit,
      persistUploaderOnSuccess,
    } = this.props;
    const { url, cropped } = this.state;
    const { options = {} } = uploader;
    const fileMetadataObject = !isEmpty(soundcastId)
      ? {
          publisherId,
          soundcastId,
          destinationField: 'imageURL',
          autoDeletePrevious: true,
        } 
      : {};

    if (!isEmpty(url) && !persistUploaderOnSuccess) {
      return (
        <div style={artStyle.wrapper || {}}>
          <div style={artStyle.cover || {}}>
            <CenteredImage>
              <img src={url} style={artStyle.image || {}} />
            </CenteredImage>
            {allowEdit && (
              <button style={artStyle.button || {}} onClick={this.resetImageUri}>
                Edit
              </button>
            )}
          </div>
        </div>
      );
    }

    return (
      <Smartuploader
        ref={ref => (this.fileuploaderRef = ref)}
        fileuploader={{
          server: '/api/upload',
          onprocessfile: this.onFilepondProcess,
          onremovefile: this.onFilepondFileRemove,
          onerror: this.onFilepondFileRemove,
          acceptedFileTypes: ['image/png', 'image/jpg', 'image/jpeg'],
          fileValidateTypeLabelExpectedTypesMap: {
            'image/png': '.png',
            'image/jpg': '.jpg',
            'image/jpeg': '.jpeg',
          },
          allowImageValidateSize: true,
          imageValidateSizeMinWidth: 1400,
          imageValidateSizeMaxWidth: 3000,
          imageValidateSizeMinHeight: 1400,
          imageValidateSizeMaxHeight: 3000,
          beforeAddFile: filepond => {
            if (withCrop && !cropped) {
              this.initFilepondImageCropperFor(filepond);
              return false;
            }

            return true;
          },
          fileRenameFunction: file => {
            const time = moment().format('x');
            const nfilename = !isEmpty(filename) ? `-${filename}-${time}` : `-${time}`;
            if (isEmpty(soundcastId)) {
              const fname = !isEmpty(filename) ? filename : nfilename;
              return `${fname}${file.extension}`;
            }

            return `${soundcastId}${nfilename}${file.extension}`;
          },
          fileMetadataObject,
          ...options,
        }}
        {...uploader}
      />
    );
  }
}

const CenteredImage = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

ImageArt.propTypes = {
  soundcastId: PropTypes.string,
  datakey: PropTypes.string,
  datakeyBlur: PropTypes.string,
  datakeyPreview: PropTypes.string,
  value: PropTypes.string,
  withBlur: PropTypes.bool,
  withPreview: PropTypes.bool,
  withCrop: PropTypes.bool,
  allowEdit: PropTypes.bool,
  persistUploaderOnSuccess: PropTypes.bool,
  autoDeletePrevious: PropTypes.bool,
  autoSave: PropTypes.bool,
  cropperHandler: PropTypes.func,
  uploader: PropTypes.object,
  onChange: PropTypes.func,
  artStyle: PropTypes.object,
  filename: PropTypes.string,
};

ImageArt.defaultProps = {
  publisherId: '',
  soundcastId: '',
  datakey: '',
  datakeyBlur: '',
  datakeyPreview: '',
  value: '',
  withBlur: false,
  withPreview: false,
  withCrop: false,
  allowEdit: true,
  persistUploaderOnSuccess: false,
  autoDeletePrevious: false,
  autoSave: false,
  cropperHandler: () => {},
  uploader: {},
  onChange: () => {},
  artStyle: {},
  filename: '',
};

export default ImageArt;
